import React, { useEffect, useMemo, useState } from 'react';
import './App.css';
import { PublicClientApplication } from '@azure/msal-browser';
import { createTheme } from '@mui/material';
import { msalConfig } from './config/msalConfig';
import { AppProviders } from './components/app/AppProviders';
import { AppBarActions, AppBarExtras } from './components/app/AppBarControls';
import AppBody from './AppBody';

function AppRoot() {
  const msalInstance = useMemo(() => new PublicClientApplication(msalConfig), []);
  const [themeMode, setThemeMode] = useState<'dark' | 'light'>(() => {
    if (typeof window === 'undefined') return 'dark';
    return window.localStorage.getItem('sa-theme') === 'light' ? 'light' : 'dark';
  });
  const [adminOpen, setAdminOpen] = useState<boolean>(false);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.theme = themeMode;
    try {
      window.localStorage.setItem('sa-theme', themeMode);
    } catch {
      // ignore storage errors
    }
  }, [themeMode]);

  const theme = useMemo(() => {
    const isDark = themeMode === 'dark';
    return createTheme({
      palette: {
        mode: themeMode,
        primary: { main: isDark ? '#10b981' : '#0f766e' },
        secondary: { main: isDark ? '#38bdf8' : '#2563eb' },
        background: {
          default: isDark ? '#14161c' : '#f4f6fb',
          paper: isDark ? '#1b1f27' : '#ffffff',
        },
        text: {
          primary: isDark ? '#e5e7eb' : '#0f172a',
          secondary: isDark ? '#94a3b8' : '#475569',
        },
      },
      typography: {
        fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
      },
      components: {
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
              backgroundColor: isDark ? '#1b1f27' : '#ffffff',
              border: isDark ? '1px solid #1f242f' : '1px solid #e2e8f0',
            },
          },
        },
        MuiCircularProgress: {
          styleOverrides: {
            root: {
              color: isDark ? '#e2e8f0' : '#0f766e',
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              backgroundColor: isDark ? '#171b22' : '#ffffff',
              borderRadius: 12,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? '#1f242f' : '#e2e8f0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? '#2b3344' : '#cbd5f5',
              },
            },
            input: { color: isDark ? '#e5e7eb' : '#0f172a' },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 12,
              fontWeight: 600,
            },
          },
        },
      },
    });
  }, [themeMode]);

  return (
    <AppProviders
      theme={theme}
      msalInstance={msalInstance}
      appBarExtras={(
        <AppBarExtras themeMode={themeMode} setThemeMode={setThemeMode} />
      )}
      appBarActions={(
        <AppBarActions onOpenAdmin={() => setAdminOpen(true)} />
      )}
    >
      <AppBody adminOpen={adminOpen} setAdminOpen={setAdminOpen} isDarkTheme={themeMode === 'dark'} />
    </AppProviders>
  );
}

export default AppRoot;
